import { useEffect, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { formatCurrency, parseAmount, toCents } from "../../utils/budgetInput";
import type { LedgerAllocationOperationType } from "../../types/accounting.types";

/**
 * ADM-621 - cellules interactives des tableaux de catégories (page Budgets et
 * drawer DRW-CATMGR), partagées pour que les deux restent iso : brouillon +
 * bouton OK dès que la valeur change, un blur sans changement range le
 * brouillon.
 *
 * Le brouillon est LOCAL à la cellule : porté par la page, il forçait la
 * reconstruction des colonnes TanStack à chaque frappe, et flexRender remonte
 * alors la cellule - l'input perdait le focus avant la première touche.
 */

/**
 * Densité maison des tableaux TanStack compacts, reprise de
 * bankMovements.columns : posée par colonne, elle s'applique aux th comme aux
 * td - c'est le même rendu que le tableau des mouvements.
 */
export const COMPACT_COLUMN_CLASS = "leading-tight !px-3 !py-2 !min-h-9";

type AccountNumberCellProps = {
  category: LedgerAllocationOperationType
  isBusy: boolean
  onSave: (category: LedgerAllocationOperationType, numeroCompte: string | null) => void
};

export const AccountNumberCell = ({ category, isBusy, onSave }: AccountNumberCellProps) => {
  const [draft, setDraft] = useState<string | null>(null);
  const saved = category.numero_compte ?? "";

  // La valeur enregistrée a changé (sauvegarde aboutie, refetch) : le
  // brouillon ne se rapporte plus à rien.
  useEffect(() => {
    setDraft(null);
  }, [saved]);

  const dirty = draft !== null && draft.trim() !== saved;

  // Le brouillon reste affiché jusqu'à ce que la valeur enregistrée arrive
  // (l'effet ci-dessus le range alors) : le vider tout de suite réaffichait
  // l'ancienne valeur le temps du refetch - un clignotement qui semblait bugué.
  const save = () => {
    if (!dirty || draft === null) return;
    const trimmed = draft.trim();
    onSave(category, trimmed || null);
  };

  return (
    <div className="flex items-center gap-1.5">
      <Input
        aria-label={`Compte comptable ${category.name}`}
        className="h-7 w-24 text-xs tabular-nums"
        placeholder="-"
        value={draft ?? saved}
        onFocus={(event) => event.target.select()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (!dirty) setDraft(null);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") save();
        }}
        disabled={isBusy}
      />
      {dirty && (
        <Button size="sm" className="h-7 px-2 text-[11px]" disabled={isBusy} onClick={save}>
          OK
        </Button>
      )}
    </div>
  );
};

type BudgetAmountCellProps = {
  category: LedgerAllocationOperationType
  savedAmount: number | null
  /** Plafond que la ligne peut porter (enveloppe − réparti ailleurs), nil sans enveloppe. */
  maxAssignable: number | null
  isBusy: boolean
  onSave: (category: LedgerAllocationOperationType, raw: string) => void
  /** « Max » proposé sur la ligne en cours de saisie (page Budgets). */
  showMax?: boolean
};

export const BudgetAmountCell = ({
  category, savedAmount, maxAssignable, isBusy, onSave, showMax = false,
}: BudgetAmountCellProps) => {
  const [draft, setDraft] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setDraft(null);
  }, [savedAmount]);

  const displayed = draft ?? (savedAmount === null ? "" : String(savedAmount));
  const parsedDraft = draft === null || draft.trim() === "" ? null : parseAmount(draft);
  const dirty = parsedDraft !== null && parsedDraft !== savedAmount;
  const overflow = parsedDraft !== null && maxAssignable !== null
    && toCents(parsedDraft) > toCents(maxAssignable);
  const canFillMax = showMax
    && isFocused
    && maxAssignable !== null
    && toCents(maxAssignable) > 0
    && (parsedDraft === null || toCents(parsedDraft) !== toCents(maxAssignable));

  const save = () => {
    if (!dirty || overflow || draft === null) return;
    onSave(category, draft);
  };

  return (
    <>
      <div className="flex items-center justify-end gap-1.5">
        <Input
          aria-label={`${category.nature === "revenue" ? "Prévision" : "Budget"} ${category.name}`}
          className={cn(
            "h-7 w-24 text-right text-xs tabular-nums",
            overflow && "border-destructive focus-visible:ring-destructive",
          )}
          inputMode="decimal"
          placeholder="-"
          aria-invalid={overflow}
          value={displayed}
          onFocus={(event) => {
            setIsFocused(true);
            event.target.select();
          }}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            setIsFocused(false);
            // Champ vidé sans rien à enregistrer : on remet le montant en
            // place plutôt que de laisser une case blanche dans la colonne.
            if (draft !== null && draft.trim() === "" && !dirty) setDraft(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") save();
          }}
        />
        {canFillMax && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11px] font-normal text-slate-600"
            aria-label={`Affecter le reste répartissable à ${category.name}`}
            title={`Affecter ${formatCurrency(maxAssignable)}`}
            // Sans cela, le clic blurerait l'input et ferait disparaître le
            // bouton avant lui.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setDraft(String(maxAssignable))}
          >
            Max
          </Button>
        )}
        {dirty && (
          <Button
            size="sm"
            className="h-7 px-2 text-[11px]"
            disabled={isBusy || overflow}
            onClick={save}
          >
            OK
          </Button>
        )}
      </div>
      {overflow && maxAssignable !== null && (
        <p role="alert" className="mt-0.5 text-right text-[11px] font-medium text-destructive">
          {maxAssignable > 0
            ? `Dépasse l'enveloppe : il reste ${formatCurrency(maxAssignable)} à répartir.`
            : "Enveloppe déjà entièrement répartie."}
        </p>
      )}
    </>
  );
};
